import * as React from 'react';
import { groupPhaseTitleForReleasePhase } from '../helpers/displayHelpers';
import { ReleaseTimelinePhase } from '../types';
import { antGrey, runwayColors } from './styles/globalStyles';
import styles from './styles/timelineStyles';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps } from '../typescriptHelpers';
import { FetchablePaginatedEntityType } from '../entityType';
import { globalMessage } from '../utils/globalMessage';
import Button from 'antd/lib/button';
import ReleasesSidebarNode from '@/components/sidebar/ReleasesSidebarNode';
import { selectCompletedReleases, selectCurrentReleases, selectUpcomingReleases } from '@/selectors/memoizedSelectors';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { useError, useIsLoading } from '@/utils/hooks/helpers';
import { ArrowDownOutlined } from '@ant-design/icons';

interface ReleaseGroupProps extends AppComponentProps {
    orgIdParam: string;
    appIdParam: string;
    groupPhase: ReleaseTimelinePhase;
    onClickLoadMore?: () => void;
    totalCount?: number;
    canLoadMore?: boolean;
}

const ReleaseGroup: React.FC<ReleaseGroupProps> = (props: ReleaseGroupProps) => {
    const groupTitle = groupPhaseTitleForReleasePhase(props.groupPhase);
    const showNodeConnectors = props.groupPhase === ReleaseTimelinePhase.Completed;

    const releases = useSelector((state: RootState) => {
        switch (props.groupPhase) {
            case ReleaseTimelinePhase.Completed:
                return selectCompletedReleases(state, props.appId);
            case ReleaseTimelinePhase.Current:
                return selectCurrentReleases(state, props.appId);
            case ReleaseTimelinePhase.Upcoming:
                return selectUpcomingReleases(state, props.appId);
            default:
                return [];
        }
    });

    const fetchCompletedReleasesIsLoading = useIsLoading(FetchablePaginatedEntityType.Releases, props.appId);
    const fetchCompletedReleasesError = useError(FetchablePaginatedEntityType.Releases, props.appId);

    const isCompletedReleasesGroup = props.groupPhase === ReleaseTimelinePhase.Completed;

    const releasesCount = releases.length;

    const showFetchMoreCompletedReleasesButton =
        isCompletedReleasesGroup &&
        props.canLoadMore !== false &&
        props.totalCount !== 0 &&
        props.totalCount !== releasesCount;

    React.useEffect(() => {
        if (fetchCompletedReleasesError && isCompletedReleasesGroup) {
            globalMessage.error(fetchCompletedReleasesError);
        }
    }, [fetchCompletedReleasesError, isCompletedReleasesGroup]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 10 }}>
            <Text
                style={{
                    ...styles.timelineHeaderStyle,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    fontWeight: 400,
                    color: antGrey.grey6,
                    letterSpacing: '0.1em',
                }}
            >
                {groupTitle}
            </Text>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {releases.map((release, index) => {
                    return (
                        <li key={release.id}>
                            <ReleasesSidebarNode
                                orgId={props.orgId}
                                appId={props.appId}
                                releaseId={release.id}
                                index={index}
                                releasesCount={releasesCount}
                                showConnectors={showNodeConnectors}
                            />
                        </li>
                    );
                })}
            </ul>
            {showFetchMoreCompletedReleasesButton && (
                <Button
                    type="text"
                    loading={fetchCompletedReleasesIsLoading}
                    onClick={props.onClickLoadMore}
                    style={{
                        ...styles.timelineHeaderStyle,
                        textTransform: 'uppercase',
                        fontSize: 12,
                        fontWeight: 400,
                        letterSpacing: '0.1em',
                        color: runwayColors.primaryLight,
                        marginTop: 10,
                        borderRadius: 0,
                        justifyContent: 'flex-start',
                        paddingLeft: 17,
                    }}
                    icon={<ArrowDownOutlined />}
                >
                    Load more...
                </Button>
            )}
        </div>
    );
};

export default ReleaseGroup;
